import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { PanelHeader, EmptyHint } from "./_shared";

function InlineAuditTrail({ entity, entityId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/audit-logs/trail/${entity}/${entityId}?limit=15`);
        if (alive) setItems(data.items || []);
      } catch (e) { if (alive) setErr(formatApiError(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [entity, entityId]);
  if (loading) return <div className="text-[11px] text-slate-500">Loading…</div>;
  if (err) return <div className="rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5 text-[11px] text-amber-800">{err}</div>;
  if (items.length === 0) return <EmptyHint message="No audit entries on this project yet." />;
  return (
    <ol className="space-y-1.5">
      {items.map((a) => (
        <li key={a.id} className="rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono-alt text-[10px] uppercase tracking-wider text-blue-700">{a.action}</span>
            <span className="text-slate-500">·</span>
            <span className="text-slate-700">{a.user_email || "system"}</span>
            <span className="text-slate-500 ml-auto tabular-nums">{formatDate(a.created_at)}</span>
          </div>
          {Array.isArray(a.diff) && a.diff.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {a.diff.slice(0, 4).map((c, i) => (
                <li key={i} className="font-mono-alt text-[10px]">
                  <span className="text-slate-600">{c.field}:</span>{" "}
                  <span className="line-through text-rose-600">{String(c.from ?? "—")}</span>
                  <span className="mx-1 text-slate-400">→</span>
                  <span className="text-emerald-700">{String(c.to ?? "—")}</span>
                </li>
              ))}
              {a.diff.length > 4 && <li className="text-[10px] text-slate-400">+{a.diff.length - 4} more</li>}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}

export default function InternalAuditPanel({ project }) {
  return (
    <div data-testid={`panel-audit-${project.id}`}>
      <PanelHeader icon={MagnifyingGlass} title="Internal Audit Trail"
        subtitle="Field-level history of every change on this project"
        action={<AuditTrailDrawer entity="project" entityId={project.id} label={project.name}
                  trigger={<Button size="sm" variant="outline">Full trail <ArrowRight size={11} className="ml-1" /></Button>} />} />
      <InlineAuditTrail entity="project" entityId={project.id} />
    </div>
  );
}
